/**
 * FLAGS — `useFeatureEnabled(key, {orgId?, courseId?})`: UNIFORM client-side
 * feature-gating layer.
 *
 * Resolves the EFFECTIVE state (dependency graph included) in a
 * course → org → platform → enabled cascade, with the same semantics as
 * the server (`platform/features.rs`):
 *  · platform: `GET /api/flags` (every row in `feature_flags`).
 *    Guest/offline ⇒ no data ⇒ default enabled (honest local-first).
 *  · org: synced `org_settings.features` (PowerSync, reactive).
 *  · course: synced `course_settings.features`.
 *
 * UX ONLY: the real authority is the server (`feature_gate` 403 + sync
 * write-paths). Hiding UI avoids the 403; it isn't the barrier.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  featureGraph,
  type FeatureOverrides,
  parseFeatureOverrides,
  resolveFeatureEffective,
} from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";

/** Effective state of a feature + the culprit key if it's blocked. */
export interface FeatureEnabledState {
  enabled: boolean;
  /** Key that blocks it (itself or a graph requirement). */
  blockedBy?: string;
}

interface PublicFlagsResponse {
  success: boolean;
  data: { key: string; enabled: boolean; rollout: number }[];
}

/**
 * Platform flags map (`key → enabled`). Cached 60s and tolerant: no
 * session or no network ⇒ empty map (everything inherits enabled).
 */
export function usePlatformFlags(): Accessor<Readonly<Record<string, boolean>>> {
  const query = createApiQuery<PublicFlagsResponse, unknown>(
    () => ["flags", "public"],
    async () => api.GET("/api/flags", {}),
    () => ({ staleTime: 60_000, retry: false, refetchOnWindowFocus: false }),
  );
  const flags = createMemo(() => {
    const out: Record<string, boolean> = {};
    // NEVER read `data` with no data yet. solid-query's `createQuery` is
    // backed by a resource: reading `.data` in `pending` state SUSPENDS the
    // tree. And this read doesn't live in a screen — it lives in the shell
    // (the sidebar resolves its feature gating right here), so the
    // boundary that catches the suspension is ABOVE the shell: Solid
    // disconnects EVERYTHING (sidebar included) from the DOM and reinserts
    // it on resolve. Measured: ~55ms of blank screen per navigation, and no
    // `<Suspense>` placed under the shell can contain it because the read
    // isn't below it.
    //
    // `isPending` is exactly "no data yet", which is the case this hook
    // already documents above as empty map ⇒ every feature inherits
    // enabled. On a refetch with previous data the state is `success` and
    // this guard doesn't kick in: the update stays reactive.
    if (query.isPending) {
      return out;
    }
    for (const f of query.data?.data ?? []) {
      out[f.key] = f.enabled;
    }
    return out;
  });
  return flags;
}

export interface UseFeatureEnabledOptions {
  /** Org to evaluate; defaults to the switcher's ACTIVE org. */
  orgId?: Accessor<string | undefined>;
  /** Course to evaluate (applies `course_settings` overrides). */
  courseId?: Accessor<string | undefined>;
  /**
   * Ignores organization and course overrides: resolves ONLY against
   * platform flags.
   *
   * Exists for `platform`-scoped features whose server gate is called with
   * `org_id = None` (today: the profile photo). Without this, the client
   * would query the active org's overrides while the server doesn't, so an
   * academy that turned the flag on for itself would see the button and get
   * a 403 when clicking it. Two different resolutions for the same flag is
   * exactly the kind of drift this hook exists to prevent.
   */
  platformOnly?: boolean;
}

/**
 * Uniform hook: is `key` effectively-enabled in this context?
 * Reactive to org switches, settings sync, and flag changes.
 */
export function useFeatureEnabled(
  key: Accessor<string> | string,
  options: UseFeatureEnabledOptions = {},
): Accessor<FeatureEnabledState> {
  const keyOf = typeof key === "string" ? () => key : key;
  const { activeOrgId } = useActiveOrg();
  // Explicit org if the caller resolves it; if its accessor returns
  // undefined it falls back to the switcher's ACTIVE org (gating's default context).
  const orgId = () => options.orgId?.() ?? activeOrgId();
  const platform = usePlatformFlags();
  const orgSettings = useOrgSettings(() => orgId());
  const courseSettings = useCourseSettings(() => options.courseId?.());
  const graph = featureGraph();

  const state = createMemo(() => {
    const org: FeatureOverrides = options.platformOnly
      ? {}
      : parseFeatureOverrides(orgSettings.data?.features);
    const course: FeatureOverrides = options.platformOnly
      ? {}
      : parseFeatureOverrides(courseSettings.data?.features);
    return resolveFeatureEffective(keyOf(), { platformMap: platform(), org, course }, graph);
  });
  return state;
}
